import React from 'react';
import { CheckoutConfig, CheckoutABTest } from '../../../types';

interface AbTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
  previewVariation: { testId: string; varId: string } | null;
  onSelectPreviewVariation: (variation: { testId: string; varId: string } | null) => void;
}

export const CheckoutStudioAbTab: React.FC<AbTabProps> = ({
  config,
  onUpdate,
  previewVariation,
  onSelectPreviewVariation,
}) => {
  const tests = config.abTests || [];

  const handleAddTest = () => {
    const newTest: CheckoutABTest = {
      id: `test-${Date.now()}`,
      name: 'Headline & Template Split Test',
      description: 'Comparing SaaS Vibe vs Clean Stripe template conversions',
      isEnabled: true,
      variations: [
        {
          id: `var-a-${Date.now()}`,
          name: 'Variation A (Control)',
          trafficWeight: 50,
          config: {},
          stats: { views: 142, conversions: 18 },
        },
        {
          id: `var-b-${Date.now()}`,
          name: 'Variation B (Minimalist)',
          trafficWeight: 50,
          config: { template: 'clean_stripe', buttonText: 'Lock In Discount Now' },
          stats: { views: 139, conversions: 24 },
        },
      ],
    };
    onUpdate({ abTests: [...tests, newTest] });
  };

  const handleToggleTest = (id: string) => {
    const updated = tests.map((t) => (t.id === id ? { ...t, isEnabled: !t.isEnabled } : t));
    onUpdate({ abTests: updated });
  };

  const handleDeleteTest = (id: string) => {
    onUpdate({ abTests: tests.filter((t) => t.id !== id) });
  };

  const handleDeclareWinner = (testId: string, variationConfig: Partial<CheckoutConfig>) => {
    onUpdate({
      ...variationConfig,
      abTests: tests.map((t) => (t.id === testId ? { ...t, isEnabled: false } : t)),
    });
    onSelectPreviewVariation(null);
  };

  return (
    <div className="space-y-4 text-left">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-slate-800 dark:text-slate-200">A/B Testing Experiments</p>
          <p className="text-[11px] text-slate-500">Split-test templates, buttons, and headlines with live tracking.</p>
        </div>
        <button
          type="button"
          onClick={handleAddTest}
          className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>New Test</span>
        </button>
      </div>

      <div className="space-y-3">
        {tests.map((test) => (
          <div key={test.id} className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-bold text-slate-900 dark:text-white">{test.name}</h4>
                <p className="text-[10px] text-slate-500">{test.description}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleToggleTest(test.id)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                    test.isEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {test.isEnabled ? 'Active' : 'Paused'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteTest(test.id)}
                  className="p-1 text-slate-400 hover:text-rose-600"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Variations List */}
            <div className="space-y-2 pt-1 border-t border-slate-200/70 dark:border-slate-800">
              {test.variations.map((v) => {
                const isPreviewing = previewVariation?.varId === v.id;
                const views = v.stats?.views || 0;
                const conv = v.stats?.conversions || 0;
                const rate = views > 0 ? ((conv / views) * 100).toFixed(1) : '0.0';

                return (
                  <div
                    key={v.id}
                    className="p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 text-xs"
                  >
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white leading-tight">{v.name}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Split: {v.trafficWeight}% • {views} Views • {conv} Conv ({rate}%)
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleDeclareWinner(test.id, v.config || {})}
                        title="Promote this variation to default checkout"
                        className="px-2 py-1 text-[10px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800"
                      >
                        Declare Winner
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          onSelectPreviewVariation(isPreviewing ? null : { testId: test.id, varId: v.id })
                        }
                        className={`px-2.5 py-1 text-[10px] font-bold rounded transition-all ${
                          isPreviewing
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200'
                        }`}
                      >
                        {isPreviewing ? 'Active' : 'Preview'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {tests.length === 0 && (
          <p className="text-xs text-slate-400 italic py-3 text-center">No A/B tests active. Click "+ New Test" to run an experiment.</p>
        )}
      </div>
    </div>
  );
};
